import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './GatorBackupUpgrade.styles';
import {
  Button,
  Checkbox,
  IconData,
  IconLoading,
  Select,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale/useLocale';
import { HgProducts } from '../../SupportForms.types';
import { V2Forms } from '@/hooks/supportForms/useSupportForms.types';

export const GatorBackupUpgrade = ({
  selectedProduct,
  opening,
  submitForm,
}) => {
  const { ticket: ticketsLocale } = useLocale();
  const { upgradeGatorBackup: upgradeGatorBackupLocale } = ticketsLocale.forms;
  const [formData, setFormData] = useState({
    domain: '',
    newProduct: '',
    agreed: false,
    agreedSelectedPlan: false,
  });

  const [productOptions, setProductOptions] = useState([]);

  const checkForm = useCallback(() => !(!!formData.newProduct && !!formData.agreed && !!formData.agreedSelectedPlan), [formData]);

  const createUpgradeOptions = useCallback(() => {
    const options = [];

    const gatorBackupCoreOption = {
      label: 'Gator Backup Core',
      value: 2,
    };

    const gatorBackupPlusOption = {
      label: 'Gator Backup Plus',
      value: 3,
    };

    const gatorBackupAdvancedOption = {
      label: 'Gator Backup Advanced',
      value: 4,
    };

    const gatorBackupMaxOption = {
      label: 'Gator Backup Max',
      value: 5,
    };

    const gatorBackupUpgradeTiers = [
      gatorBackupCoreOption,
      gatorBackupPlusOption,
      gatorBackupAdvancedOption,
      gatorBackupMaxOption,
    ];

    const gatorBackupTierPids = [
      HgProducts.GATOR_BACKUP_CORE,
      HgProducts.GATOR_BACKUP_PLUS,
      HgProducts.GATOR_BACKUP_ADVANCED,
      HgProducts.GATOR_BACKUP_MAX,
    ];

    const currentTierIndex = gatorBackupTierPids.indexOf(selectedProduct?.pid);

    if (currentTierIndex !== -1) {
      gatorBackupUpgradeTiers.forEach((tierOption, index) => {
        if (index > currentTierIndex) {
          options.push(tierOption);
        }
      });
    }

    setProductOptions(options);
  }, [selectedProduct]);

  useEffect(() => {
    createUpgradeOptions();
  }, [createUpgradeOptions]);


  const mountGatorBackupTag = useCallback(() => {
    const rules = {
      [HgProducts.GATOR_BACKUP_CORE]: 'gatorbackup_core',
      [HgProducts.GATOR_BACKUP_PLUS]: 'gatorbackup_plus',
      [HgProducts.GATOR_BACKUP_ADVANCED]: 'gatorbackup_advanced',
      [HgProducts.GATOR_BACKUP_MAX]: 'gatorbackup_max',
    };

    return rules[selectedProduct.pid];
  }, [
    selectedProduct,
  ]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    const data = {
      locale: ticketsLocale,
      automation: false,
      pid: selectedProduct.pid,
      product: selectedProduct.id,
      subject: ticketsLocale.forms.names.upgradeGatorBackup,
      server_value: selectedProduct.serverhostname,
      domain_value: selectedProduct.domain,
      key: V2Forms.UPGRADE_GATOR_BACKUP,
      tags: [
        'whmcs_form_financeiro_upgrade_gatorbackup',
        mountGatorBackupTag(),
      ],
      body: `
        • ${upgradeGatorBackupLocale.body.product(selectedProduct)}
        • ${upgradeGatorBackupLocale.body.request}
        • ${upgradeGatorBackupLocale.body.newProduct(formData.newProduct)}
        • ${upgradeGatorBackupLocale.body.agreed(formData.agreed)}
        • ${upgradeGatorBackupLocale.body.agreedSelectedPlan(formData.agreedSelectedPlan)}
        • ${upgradeGatorBackupLocale.body.productId(selectedProduct)}
      `,
    };

    submitForm && submitForm(data);
  }, [
    ticketsLocale,
    selectedProduct,
    formData,
    submitForm,
    upgradeGatorBackupLocale,
    mountGatorBackupTag,
  ]);

  const handleChange = useCallback(({ key, value }) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  }, [
    formData,
  ]);

  return (
    <Styles.Form onSubmit={e => handleSubmit(e)}>
      <Styles.Info>
        <IconData />
        {upgradeGatorBackupLocale.info}
      </Styles.Info>

      <Styles.Title>
        {upgradeGatorBackupLocale.title}
      </Styles.Title>

      <Styles.InputsWrapper>
        <Select
          name="newProduct"
          testId="newProduct"
          placeholder={upgradeGatorBackupLocale.placeholders.newProduct}
          label={formData.newProduct ? upgradeGatorBackupLocale.placeholders.newProduct : ''}
          options={productOptions}
          value={formData.newProduct}
          setValue={label => handleChange({ key: 'newProduct', value: label })}
          withScroll
        />
      </Styles.InputsWrapper>

      <Styles.InputsWrapper>
        <Styles.CheckboxLabel
          htmlFor="#readTerms"
          data-testid="checkReadTerms"
          onClick={() => handleChange({ key: 'agreed', value: !formData.agreed })}
        >
          <Checkbox checked={formData.agreed} id="readTerms" />
          <Styles.Text>
            {upgradeGatorBackupLocale.placeholders.agreed}
          </Styles.Text>
        </Styles.CheckboxLabel>

        <Styles.CheckboxLabel
          htmlFor="#readTermsSelectedPlan"
          data-testid="checkReadTermsSelectedPlan"
          onClick={() => handleChange({ key: 'agreedSelectedPlan', value: !formData.agreedSelectedPlan })}
        >
          <Checkbox checked={formData.agreedSelectedPlan} id="readTermsSelectedPlan" />
          <Styles.Text>
            {upgradeGatorBackupLocale.placeholders.agreedSelectedPlan}
          </Styles.Text>
        </Styles.CheckboxLabel>
      </Styles.InputsWrapper>


      <Styles.ButtonWrapper>
        <Button
          size="large"
          label={opening ? <IconLoading /> : upgradeGatorBackupLocale.send}
          disabled={checkForm() || opening}
          type="submit"
          testId="submit"
        />
      </Styles.ButtonWrapper>
    </Styles.Form>
  );
};

export default GatorBackupUpgrade;
